import { z } from 'zod';
import type { ActionNodeExecuteConfig } from './interfaces.js';

/**
 * The flow engine's caller context for generic action invocations.
 *
 * The host action registry (`ctx.services.actionRegistry`) is flow-agnostic:
 * an invocation carries only `provider` / `operation` / `params` /
 * `organizationId` plus an opaque `callerContext` bag. The flow engine fills
 * that bag with its run context via {@link flowActionCallerContext}; a handler
 * that is deliberately flow-coupled (agent_control, built-in profiling flows)
 * recovers it with {@link parseFlowActionCallerContext} instead of probing the
 * bag ad hoc.
 */
export interface FlowActionCallerContext {
  /** The flow run ID. */
  runId: string;
  /** The flow ID being executed. */
  flowId: string;
  /** The current node ID within the flow. */
  nodeId: string;
  /** Trigger data from the flow initiator. */
  triggerData: Record<string, unknown> | null;
  /** State passed between nodes. */
  state: Record<string, unknown>;
  /**
   * The effective actor for this run (`FlowRun.userId`): the member who
   * triggered it, or the flow's owner when that owner is a member. An action
   * node that launches an agent stamps the run's owner from this, so a
   * human-triggered launch belongs to that human instead of the whole org.
   * Null when neither exists; the launching action resolves the automation's
   * own principal in that case.
   */
  userId: string | null;
}

const FlowActionCallerContextSchema = z.object({
  runId: z.string().min(1),
  flowId: z.string().min(1),
  nodeId: z.string().min(1),
  triggerData: z.record(z.string(), z.unknown()).nullable(),
  state: z.record(z.string(), z.unknown()),
  // Tolerant on the read side: an action that does not launch anything should
  // not fail to parse its context because the actor is absent. Producers are
  // held to it by the required field on {@link FlowActionCallerContext}.
  userId: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
});

/**
 * Build the `callerContext` bag the engine attaches when dispatching an
 * action node through the generic action registry.
 */
export function flowActionCallerContext(
  config: Pick<
    ActionNodeExecuteConfig,
    'runId' | 'flowId' | 'nodeId' | 'triggerData' | 'state' | 'userId'
  >,
): Record<string, unknown> {
  const context: FlowActionCallerContext = {
    runId: config.runId,
    flowId: config.flowId,
    nodeId: config.nodeId,
    triggerData: config.triggerData,
    state: config.state,
    userId: config.userId ?? null,
  };
  return context as unknown as Record<string, unknown>;
}

/**
 * Parse a generic invocation's `callerContext` back into the flow engine's
 * run context. Throws when the invocation did not come from the flow engine —
 * flow-coupled handlers must fail loudly rather than run with fabricated ids.
 */
export function parseFlowActionCallerContext(
  callerContext: Record<string, unknown> | undefined,
): FlowActionCallerContext {
  const parsed = FlowActionCallerContextSchema.safeParse(callerContext);
  if (!parsed.success) {
    throw new Error(
      'Action invocation is missing the flow caller context; this action can only run from a flow',
    );
  }
  return parsed.data;
}
